import { useQuery } from "@tanstack/react-query";
import {
  TabsProps,
  Typography,
  Flex,
  Spin,
  Tabs,
  Button,
  ConfigProvider,
  Divider,
  Modal,
  Card,
} from "antd";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import PageHeading from "components/PageHeading.component";
import { useDeleteScheduleV2 } from "hooks/mutations/schedule-v2";
import { bookingsByScheduleV2Options } from "hooks/queries/booking-v2";
import { scheduleV2DetailOptions } from "hooks/queries/schedule-v2";
import { waitlistByScheduleV2Options } from "hooks/queries/waitlist-v2";
import { useBusinessSettings } from "hooks/queries/business-settings";
import UpdateScheduleModal from "components/modals/UpdateScheduleModal.component";
import { ReactNode, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import PATHS from "shared/constants/paths";
import { WAITLIST_CAP } from "shared/constants/waitlist";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { Schedule } from "shared/types";
import { BookingStatus, ClassCategory } from "shared/types/enum";
import {
  formatDateToLocalString,
  isSeatHoldingBookingStatus,
} from "shared/utils";
import { now } from "shared/utils/dayjs";
import { ScheduleResumeDraft } from "shared/utils/scheduleResumeDraft";
import {
  ScheduleStatusBadge,
  StatusBadge,
} from "./component/ScheduleStatusBadge";
import ScheduleBooking from "./ScheduleBooking";
import ScheduleWaitlist from "./ScheduleWaitlist";
import { textPx } from "shared/constants/zoom";

const TAB_KEY = {
  Information: "1",
  Bookings: "2",
  Waitlist: "3",
} as const;

// Key-value row (prototype .kv): semibold key on a fixed column, muted value.
const KeyValueRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Flex align="center" gap={12} style={{ padding: "11px 0" }}>
    <Typography.Text
      strong
      style={{ fontSize: textPx(13.5), minWidth: 130, color: colors.ink }}
    >
      {label}
    </Typography.Text>
    <Typography.Text style={{ fontSize: textPx(13.5), color: colors.ink2 }}>
      {children}
    </Typography.Text>
  </Flex>
);

const ScheduleDetailsCard = ({ schedule }: { schedule: Schedule }) => {
  const hasScheduleStarted = now().isAfter(schedule.start_time);
  // Editing stays time-restricted; deletion is allowed at any time and is only
  // blocked once the schedule is already deleted.
  const isScheduleUpdateDisabled = schedule.is_cancelled || hasScheduleStarted;
  const isScheduleDeleted = schedule.is_cancelled;
  const { mutate: deleteSchedule } = useDeleteScheduleV2();

  // Deleting a schedule is irreversible (all bookings cancelled + refunded), so
  // confirm before committing. On a class that has started, the pop-up also
  // says what that does to its attended bookings and its numbers (System
  // Review B30): Balance deletes sessions that ran to void them, which stays
  // allowed.
  const deleteScheduleModal = () => {
    Modal.warning({
      // Prototype chrome: amber alert dot beside the title, muted explainer,
      // Kembali dismiss + red destructive Batalkan Jadwal.
      icon: null,
      width: 400,
      title: (
        <ModalHeader
          tone="amber"
          title={translate("messages.warning.cancel_schedule.title")}
        />
      ),
      content: (
        <Typography.Text style={modalSubtitleStyle}>
          {translate(
            // Read when the pop-up opens, not when the card rendered: a page
            // left open across the class's start must say it has started.
            now().isAfter(schedule.start_time)
              ? "messages.warning.cancel_schedule.content_started"
              : "messages.warning.cancel_schedule.content",
          )}
        </Typography.Text>
      ),
      closable: true,
      maskClosable: true,
      // Button system: solid-ink "Kembali" dismiss, red destructive action.
      footer: (
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
            <Button onClick={() => Modal.destroyAll()}>
              {translate("button.back")}
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => {
                deleteSchedule({
                  schedule_id: schedule.schedule_id,
                  occurrence_timestamp: schedule.occurrence_timestamp,
                });
                Modal.destroyAll();
              }}
            >
              {translate("button.cancel_schedule")}
            </Button>
          </Flex>
        </ConfigProvider>
      ),
      onCancel() {
        Modal.destroyAll();
      },
    });
  };

  const deleteRecurringScheduleModal = () => {
    Modal.warning({
      icon: null,
      width: 400,
      title: (
        <ModalHeader
          tone="amber"
          title={translate("messages.warning.delete_recurring_schedule.title")}
        />
      ),
      content: (
        <Typography.Text style={modalSubtitleStyle}>
          {translate(
            // Read when the pop-up opens, not when the card rendered: a page
            // left open across the class's start must say it has started.
            now().isAfter(schedule.start_time)
              ? "messages.warning.delete_recurring_schedule.content_started"
              : "messages.warning.delete_recurring_schedule.content",
          )}
        </Typography.Text>
      ),
      closable: true,
      maskClosable: true,
      footer: (
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
            <Button onClick={() => Modal.destroyAll()}>
              {translate("button.back")}
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => {
                deleteSchedule({
                  schedule_id: schedule.schedule_id,
                  occurrence_timestamp: schedule.occurrence_timestamp,
                  include_all: true,
                });
                Modal.destroyAll();
              }}
            >
              {translate("button.delete")}{" "}
              {translate("schedule.recurring_schedule")}
            </Button>
          </Flex>
        </ConfigProvider>
      ),
      onCancel() {
        Modal.destroyAll();
      },
    });
  };

  return (
    <Card>
      <Flex vertical>
        <Typography.Title
          level={5}
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: textPx(17),
            letterSpacing: "-0.01em",
          }}
        >
          {translate("title.scheduling_information")}
        </Typography.Title>
        <Typography.Text
          style={{ color: colors.ink2, fontSize: textPx(13), marginTop: 4 }}
        >
          {translate("subtitle.scheduling_information")}
        </Typography.Text>
        <div style={{ marginTop: 18 }}>
          <KeyValueRow label="Status">
            <ScheduleStatusBadge schedule={schedule} />
          </KeyValueRow>
          <KeyValueRow label={translate("label.class_name")}>
            {schedule.class_type.name}
          </KeyValueRow>
          <KeyValueRow label={translate("label.instructor_name")}>
            {schedule.instructor_name}
          </KeyValueRow>
          <KeyValueRow label={translate("label.start_time")}>
            {formatDateToLocalString(schedule.start_time)}
          </KeyValueRow>
          <KeyValueRow label={translate("label.end_time")}>
            {formatDateToLocalString(schedule.end_time)}
          </KeyValueRow>
        </div>
        <Divider style={{ margin: "18px 0" }} />
        <Flex gap={10} align="flex-start">
          {/* Navy primary (button system) — themed inside UpdateScheduleModal. */}
          <UpdateScheduleModal
            schedule={schedule}
            disabled={isScheduleUpdateDisabled}
          />
          {/* Intentional exception: soft-red (red text on light-red pill)
              since it sits inline among page actions, not in a modal footer. */}
          <Button
            color="danger"
            variant="filled"
            disabled={isScheduleDeleted}
            onClick={deleteScheduleModal}
          >
            {translate("button.delete")} {translate("schedule.name")}
          </Button>
          {schedule.is_recurring && (
            <Button
              color="danger"
              variant="outlined"
              disabled={isScheduleDeleted}
              onClick={deleteRecurringScheduleModal}
            >
              {translate("button.delete")}{" "}
              {translate("schedule.recurring_schedule")}
            </Button>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

const ScheduleDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { scheduleId } = useParams();
  const [searchParams] = useSearchParams();
  const occurrenceTimestamp =
    searchParams.get("occurrence_timestamp") ?? undefined;

  const {
    data: schedule,
    isLoading: isScheduleLoading,
    isError,
  } = useQuery(
    scheduleV2DetailOptions(
      scheduleId && occurrenceTimestamp
        ? { schedule_id: scheduleId, occurrence_timestamp: occurrenceTimestamp }
        : undefined
    )
  );

  useEffect(() => {
    if (!scheduleId || !occurrenceTimestamp || isError) {
      // Replace, not push: a broken detail URL must not stay in history
      // where Back would bounce off it.
      navigate(PATHS.SCHEDULE, { replace: true });
    }
  }, [scheduleId, occurrenceTimestamp, isError, navigate]);

  const { data: bookings } = useQuery(
    bookingsByScheduleV2Options(
      scheduleId ?? "",
      occurrenceTimestamp ?? "",
    ),
  );
  const { data: waitlistEntries } = useQuery(
    waitlistByScheduleV2Options(
      scheduleId ?? "",
      occurrenceTimestamp ?? "",
    ),
  );
  // Configured waitlist cap for the "x/y Waitlist" chip (falls back to the
  // default while the settings query loads).
  const { data: businessSettings } = useBusinessSettings();
  const waitlistCapacity = businessSettings?.waitlist_capacity ?? WAITLIST_CAP;

  const confirmedCount = (bookings ?? []).filter((b) =>
    isSeatHoldingBookingStatus(b.booking_status),
  ).length;
  // Per-schedule snapshot first (frozen at creation), falling back to the live
  // class max for schedules created before capacity was snapshotted. Matches the
  // server's effectiveCapacity so the "x/y Booking" indicator doesn't shift when
  // the class's max is later edited.
  const capacity = schedule?.capacity ?? schedule?.class_type.max_participants ?? 0;
  // Chip shows the LIVE queue against the live cap, matching the cap gate. (It
  // used to count expired snapshot rows too, so x/y could read overfull like
  // 7/5 while Add was still enabled.)
  const waitlistLength = (waitlistEntries ?? []).filter(
    (e) => e.status === BookingStatus.Waitlist,
  ).length;
  const isOpenClass = schedule?.class_type.type === ClassCategory.Open;
  // With the waitlist switched off the tab and its chip are noise, except on
  // a class that still has waitlist records (closed earlier at its cutoff).
  const waitlistOff = businessSettings?.waitlist_enabled === false;
  const showWaitlist = isOpenClass && (!waitlistOff || (waitlistEntries?.length ?? 0) > 0);

  const [activeTab, setActiveTab] = useState<string>(TAB_KEY.Information);
  const onWaitlistAdded = () => setActiveTab(TAB_KEY.Waitlist);
  const [resumeCustomerId, setResumeCustomerId] = useState<string>();
  const [resumeBookingId, setResumeBookingId] = useState<string>();
  const [resumeWaitlistId, setResumeWaitlistId] = useState<string>();

  // Resuming after a round trip to a client's detail page to buy a package
  // for the "Booking Baru" / "Tambah ke Waitlist" modals (see
  // NewBookingModal.component.tsx / AddToWaitlistModal.component.tsx) —
  // switch to the Bookings tab and hand the customer id down through
  // ScheduleBooking -> PrimaryAction to whichever modal picks it up.
  // Also handles the booking-ref search deep link (BookingRefSearch.component.tsx):
  // switch to the Bookings tab and hand the booking id down so
  // ScheduleBooking can reopen its detail drawer. A waitlist entry's code
  // goes to the Waitlist tab and ScheduleWaitlist's drawer instead.
  useEffect(() => {
    const draft = (
      location.state as { scheduleDraft?: ScheduleResumeDraft } | null
    )?.scheduleDraft;
    if (draft && (draft.kind === "new-booking" || draft.kind === "add-waitlist")) {
      setActiveTab(TAB_KEY.Bookings);
      setResumeCustomerId(draft.customerId);
      navigate(location.pathname + location.search, { replace: true });
    } else if (draft && draft.kind === "open-booking" && draft.waitlist) {
      setActiveTab(TAB_KEY.Waitlist);
      setResumeWaitlistId(draft.bookingId);
      navigate(location.pathname + location.search, { replace: true });
    } else if (draft && draft.kind === "open-booking") {
      setActiveTab(TAB_KEY.Bookings);
      setResumeBookingId(draft.bookingId);
      navigate(location.pathname + location.search, { replace: true });
    }
    // Run once on mount — the draft only arrives with the initial navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items: TabsProps["items"] = [
    {
      key: TAB_KEY.Information,
      label: translate("tabs.information"),
      children: schedule && <ScheduleDetailsCard schedule={schedule} />,
    },
    {
      key: TAB_KEY.Bookings,
      label: translate("tabs.bookings"),
      children: schedule && (
        <ScheduleBooking
          schedule={schedule}
          onWaitlistAdded={onWaitlistAdded}
          resumeCustomerId={resumeCustomerId}
          onResumeConsumed={() => setResumeCustomerId(undefined)}
          resumeBookingId={resumeBookingId}
          onResumeBookingConsumed={() => setResumeBookingId(undefined)}
        />
      ),
    },
    ...(showWaitlist
      ? [
          {
            key: TAB_KEY.Waitlist,
            label: translate("tabs.waitlist"),
            children: schedule && (
              <ScheduleWaitlist
                schedule={schedule}
                onWaitlistAdded={onWaitlistAdded}
                resumeEntryId={resumeWaitlistId}
                onResumeEntryConsumed={() => setResumeWaitlistId(undefined)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      {isScheduleLoading ? (
        <Flex
          style={{
            justifyContent: "center",
            alignItems: "center",
            height: "100vh ",
          }}
        >
          <Spin />
        </Flex>
      ) : (
        <>
          <PageHeading title={translate("title.schedule_detail")} />
          {/* Every class shows its seats; who holds them is on the Booking
              tab (the Informasi card names nobody, open or private). Only an
              open class has a waitlist. Counts, not states: no dot. */}
          <Flex gap={8} style={{ marginBottom: 16 }}>
            <StatusBadge
              tone="navy"
              label={`${confirmedCount}/${capacity} Booking`}
              dot={false}
            />
            {isOpenClass && !waitlistOff && (
              <StatusBadge
                tone="purple"
                label={`${waitlistLength}/${waitlistCapacity} Waitlist`}
                dot={false}
              />
            )}
          </Flex>
          <Tabs
            // A waitlist link (booking-ref search, resume) to a class whose
            // waitlist tab is hidden lands on the bookings instead.
            activeKey={
              !showWaitlist && activeTab === TAB_KEY.Waitlist ? TAB_KEY.Bookings : activeTab
            }
            onChange={setActiveTab}
            size="small"
            items={items}
          />
        </>
      )}
    </>
  );
};

export default ScheduleDetails;
